import { apiClient } from '@/config/api';
import type { HeaderResponse } from '@/types/header';

const HEADER_ENDPOINTS = {
    BASE: '/layout-manager',
    HEADER: '/layout-manager/header',
    HEADER_UPLOAD: '/layout-manager/header/upload',
    FOOTER: '/layout-manager/footer',
};

export interface UpdateHeaderFormData {
    title?: string;
    title_seo?: string;
    description_meta?: string;
    script?: string;
    logo?: File;
    logoId?: string;
    logoAlt?: string;
    logoTitle?: string;
    logoLinkImage?: string;
    logoCaption?: string;
    logoRel?: string;
    logoTarget?: string;
    favicon?: File;
    faviconId?: string;
    faviconAlt?: string;
    faviconTitle?: string;
    faviconLinkImage?: string;
    faviconCaption?: string;
    faviconRel?: string;
    faviconTarget?: string;
    // Robots Meta
    robotsIndex?: boolean;
    robotsFollow?: boolean;
    robotsNoarchive?: boolean;
    robotsNosnippet?: boolean;
    robotsNoimageindex?: boolean;
    robotsNocache?: boolean;
    robotsMaxVideoPreview?: number;
    robotsMaxImagePreview?: 'none' | 'standard' | 'large';
    robotsMaxSnippet?: number;
    // Verification Meta
    verificationGoogle?: string;
    verificationBing?: string;
    verificationYandex?: string;
    verificationYahoo?: string;
    verificationAhrefs?: string;
    // Google Analytics
    googleAnalyticsId?: string;
}

export const informationService = {
    /**
     * Lấy thông tin header
     */
    async getInformation(): Promise<HeaderResponse> {
        return apiClient.get(HEADER_ENDPOINTS.HEADER);
    },

    /**
     * Cập nhật thông tin header với FormData
     */
    async updateInformation(data: UpdateHeaderFormData): Promise<HeaderResponse> {
        const formData = new FormData();

        // Thêm text fields
        if (data.title !== undefined) formData.append('title', data.title);
        if (data.title_seo !== undefined) formData.append('title_seo', data.title_seo);
        if (data.description_meta !== undefined) formData.append('description_meta', data.description_meta);
        if (data.script !== undefined) formData.append('script', data.script);

        // Thêm logo file và metadata
        if (data.logo) {
            formData.append('logo', data.logo);
        }
        if (data.logoId) formData.append('logoId', data.logoId);
        if (data.logoAlt) formData.append('logoAlt', data.logoAlt);
        if (data.logoTitle) formData.append('logoTitle', data.logoTitle);
        if (data.logoLinkImage) formData.append('logoLinkImage', data.logoLinkImage);
        if (data.logoCaption) formData.append('logoCaption', data.logoCaption);
        if (data.logoRel) formData.append('logoRel', data.logoRel);
        if (data.logoTarget) formData.append('logoTarget', data.logoTarget);

        // Thêm favicon file và metadata
        if (data.favicon) {
            formData.append('favicon', data.favicon);
        }
        if (data.faviconId) formData.append('faviconId', data.faviconId);
        if (data.faviconAlt) formData.append('faviconAlt', data.faviconAlt);
        if (data.faviconTitle) formData.append('faviconTitle', data.faviconTitle);
        if (data.faviconLinkImage) formData.append('faviconLinkImage', data.faviconLinkImage);
        if (data.faviconCaption) formData.append('faviconCaption', data.faviconCaption);
        if (data.faviconRel) formData.append('faviconRel', data.faviconRel);
        if (data.faviconTarget) formData.append('faviconTarget', data.faviconTarget);

        // Thêm robots meta
        if (data.robotsIndex !== undefined) formData.append('robotsIndex', String(data.robotsIndex));
        if (data.robotsFollow !== undefined) formData.append('robotsFollow', String(data.robotsFollow));
        if (data.robotsNoarchive !== undefined) formData.append('robotsNoarchive', String(data.robotsNoarchive));
        if (data.robotsNosnippet !== undefined) formData.append('robotsNosnippet', String(data.robotsNosnippet));
        if (data.robotsNoimageindex !== undefined) formData.append('robotsNoimageindex', String(data.robotsNoimageindex));
        if (data.robotsNocache !== undefined) formData.append('robotsNocache', String(data.robotsNocache));
        if (data.robotsMaxVideoPreview !== undefined) formData.append('robotsMaxVideoPreview', String(data.robotsMaxVideoPreview));
        if (data.robotsMaxImagePreview !== undefined) formData.append('robotsMaxImagePreview', data.robotsMaxImagePreview);
        if (data.robotsMaxSnippet !== undefined) formData.append('robotsMaxSnippet', String(data.robotsMaxSnippet));

        // Thêm verification meta
        if (data.verificationGoogle !== undefined) formData.append('verificationGoogle', data.verificationGoogle);
        if (data.verificationBing !== undefined) formData.append('verificationBing', data.verificationBing);
        if (data.verificationYandex !== undefined) formData.append('verificationYandex', data.verificationYandex);
        if (data.verificationYahoo !== undefined) formData.append('verificationYahoo', data.verificationYahoo);
        if (data.verificationAhrefs !== undefined) formData.append('verificationAhrefs', data.verificationAhrefs);

        // Thêm Google Analytics ID
        if (data.googleAnalyticsId !== undefined) formData.append('googleAnalyticsId', data.googleAnalyticsId);

        return apiClient.put(HEADER_ENDPOINTS.HEADER_UPLOAD, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    /**
     * Lấy thông tin footer
     */
    async getFooter(): Promise<any> {
        return apiClient.get(HEADER_ENDPOINTS.FOOTER);
    },

    /**
     * Cập nhật thông tin footer
     */
    async updateFooter(data: UpdateFooterDto): Promise<any> {
        return apiClient.put(HEADER_ENDPOINTS.FOOTER, data);
    },
};

export interface UpdateFooterDto {
    column1?: string;
    column2?: string;
    column3?: string;
    column4?: string;
    column5?: string;
}
